/**
 * Auth provider — bridges the rest of the app to a Zitadel-backed
 * session. Two modes (matching the OIDC envs at build time):
 *
 *   - **OIDC configured** (VITE_OIDC_AUTHORITY + VITE_OIDC_CLIENT_ID
 *     set): wraps react-oidc-context's <AuthProvider> with PKCE +
 *     Authorization Code flow. The SPA's /login route is the
 *     configured ZITADEL_OIDC_DEFAULTLOGINURLV2 — when there's no
 *     ?authRequest in the URL, initiateOidcRedirect() fires
 *     signinRedirect() so Zitadel mints one and 302s back to /login
 *     with ?authRequest=…. The form there POSTs to /v1/platform/auth/login,
 *     the backend talks to Zitadel's Session API (using the
 *     loginclient-sa PAT, server-side only), and returns the
 *     callbackUrl the SPA navigates the window to. The PKCE code
 *     exchange on /auth/callback is still react-oidc-context.
 *
 *   - **OIDC not configured** (no VITE_OIDC_* envs, e.g. Arjun's
 *     UI-only dev loop): falls back to STUB_USER. signIn/signUp
 *     return a synthetic success with callbackUrl="/app" so the form
 *     code paths are identical in both modes.
 *
 * See:
 *  - apps/web/src/lib/oidc-config.ts
 *  - apps/web/src/lib/tab-session.ts (the sign-in shared across open tabs)
 *  - apps/web/src/lib/api-client.ts (token injection)
 *  - docs/decisions/2026-05-30_spa-owns-login-ui.md
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
  AuthProvider as OidcAuthProvider,
  hasAuthParams,
  useAuth as useOidcAuth,
} from "react-oidc-context";

import { isOidcConfigured, tabSession, userManager } from "./oidc-config";
import { setAccessToken, api, ApiClientError } from "./api-client";
import { forgetNavPages } from "@/lib/nav/return-to";
import { mapProducts, type SuiteProduct, type SuiteProductWire } from "@/lib/suite";
import { isPersonName } from "@/lib/settings/display-name";

export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  /**
   * Org memberships are not derivable from OIDC claims alone — the
   * platform owns that mapping. Empty in OIDC mode until a /v1/me
   * endpoint ships; populated by STUB_USER in stub mode for the dev
   * loop's benefit.
   */
  orgMemberships: Array<{
    orgId: string;
    orgName: string;
    roles: string[];
    /** org_branding.vendor_org_id — set when the foundry was onboarded via a
     *  vendor (Versatile); drives the dual Sahas+Versatile logo. */
    viaVendorOrgId?: string | null;
    /** orgs.name of the vendor — the co-brand wordmark (generic per vendor). */
    vendorName?: string | null;
    /** True when this org is itself a vendor (a vendor_orgs row) — gates the
     *  vendor-scoped nav + Fleet landing. */
    isVendor?: boolean;
    /** The role's capability bundle from /auth/me — drives can(perm). */
    permissions: string[];
    /** The suite products the org holds, Twin first, with the member's role in each (lib/suite). Empty from an older
     *  API. */
    products?: SuiteProduct[];
  }>;
  /** Platform super-admin (from /v1/platform/auth/me). Gates the
   *  cross-org console nav. Undefined until auth/me resolves. */
  isPlatformAdmin?: boolean;
  /** Vendor-admin (from /v1/platform/auth/me) — admins a vendor_orgs parent
   *  (Versatile). Gates the vendor-portal nav. Undefined until auth/me. */
  isVendorAdmin?: boolean;
  /**
   * Sign-up approval state (from /v1/platform/auth/me). New users land in
   * 'pending' and must submit the registration form, then be approved by a
   * platform admin. Grandfathered/legacy users resolve to 'approved' and pass
   * straight through. Undefined until auth/me resolves (treated as 'approved'
   * by the RequireApproved guard so a transient fetch doesn't gate anyone).
   */
  approvalStatus?: "pending" | "approved" | "rejected";
  /** True once the user has submitted the registration form — distinguishes a
   *  brand-new pending user (→ /register) from one awaiting review
   *  (→ /pending-approval). */
  approvalSubmitted?: boolean;
  /**
   * The person's display name on the platform (users.display_name from
   * /v1/platform/auth/me): the name reports and comments carry, which they
   * set under Settings, Security and privacy (PATCH /v1/platform/me). It is
   * their email until a name is set or filled in from the sign-in profile.
   * Undefined until auth/me resolves.
   */
  displayName?: string;
}

/**
 * Result of a sign-in or sign-up attempt. `ok: true` → SPA navigates
 * window.location to callbackUrl. `ok: false` → SPA shows the
 * message-table entry for code via the toast system.
 */
export type AuthAttemptResult =
  | { ok: true; callbackUrl: string }
  | { ok: false; code: string };

export interface SignUpInput {
  email: string;
  password: string;
  givenName: string;
  familyName: string;
  authRequestId: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * True once the platform identity (/v1/platform/auth/me — org memberships
   * + roles) has resolved, so consumers can distinguish "still loading
   * memberships" from "loaded, genuinely zero orgs". The no-org onboarding
   * guard waits on this to avoid bouncing a member through /onboarding during
   * the sub-second auth/me fetch. Always true in stub mode.
   */
  identityLoaded: boolean;
  /**
   * Authenticate against the existing AuthRequest. The form code
   * uses the returned callbackUrl with window.location.assign — it's
   * cross-origin in OIDC mode (Zitadel's host) and same-origin in
   * stub mode (just /app).
   */
  signIn: (
    loginName: string,
    password: string,
    authRequestId: string,
  ) => Promise<AuthAttemptResult>;
  signUp: (input: SignUpInput) => Promise<AuthAttemptResult>;
  /**
   * Kick off Zitadel's /oauth/v2/authorize so it can mint an
   * AuthRequest and redirect back to /login?authRequest=…. Used when
   * the SPA hits /login without an authRequest param (a direct nav
   * to /login, or the RequireAuth route guard sending a not-yet-
   * authed user). Stub mode is a no-op.
   */
  initiateOidcRedirect: () => void;
  logout: () => void;
  /**
   * Re-fetch /v1/platform/auth/me to refresh org memberships + roles +
   * platform-admin flag without a full reload. Called after a mutation
   * that changes membership (e.g. onboarding creates an org, accepting an
   * invite) so the OrgProvider sees the new org immediately. No-op in stub
   * mode.
   */
  refreshIdentity: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const STUB_USER: User = {
  id: "user_stub_dev",
  email: "dev@sahas.com",
  name: "Dev User",
  avatarUrl: null,
  orgMemberships: [
    {
      orgId: "org_stub_dev",
      orgName: "Dev Org",
      roles: ["admin"],
      // Dev stub: full owner bundle so can(perm) passes in no-API mode.
      permissions: [
        "data.read", "capture.write", "gateway.commission", "model.write",
        "dashboards.write", "alarms.write", "reports.write", "org.admin", "org.owner",
      ],
    },
  ],
  // Stub/UI-only dev loop is never gated by the approval flow.
  approvalStatus: "approved",
  approvalSubmitted: true,
};

/**
 * Shared POST helper that turns an ApiClientError into the discriminated
 * {ok: false, code} shape signIn/signUp consumers expect.
 */
async function postAuth(
  path: string,
  body: Record<string, string>,
): Promise<AuthAttemptResult> {
  try {
    const resp = await api.post<{ callbackUrl: string }>(path, body);
    return { ok: true, callbackUrl: resp.callbackUrl };
  } catch (e) {
    if (e instanceof ApiClientError) {
      const errBody = e.body as { error?: string } | null;
      const code = errBody?.error ?? "unknown";
      return { ok: false, code };
    }
    return { ok: false, code: "network_error" };
  }
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

/**
 * The auth state where the app provides it, or null outside it: a piece that also renders alone (a test, a view
 * rendered without the app) reads who is signed in without requiring the provider.
 */
export function useOptionalAuth(): AuthState | null {
  return useContext(AuthContext);
}

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Tokenless staging dev-mode OVERRIDE (VITE_AUTH_DEV_MODE=true): wins over OIDC
  // so the staging build (which ALSO sets VITE_OIDC_* for its login page) opens
  // straight into the backend's injected AUTH_DEV_MODE principal via
  // /v1/platform/auth/me — no Zitadel sign-in. A prod build never sets this flag,
  // so prod still takes the OIDC branch below.
  if (import.meta.env.VITE_AUTH_DEV_MODE === "true") {
    return <DevAuthProvider>{children}</DevAuthProvider>;
  }
  if (isOidcConfigured() && userManager !== null) {
    return (
      <SessionHandoff>
        <OidcAuthProvider
          userManager={userManager}
          // When the OIDC redirect lands at /auth/callback?code=…, the
          // provider processes the code, stores tokens, and fires this
          // callback. Strip code+state from the URL so a reload doesn't
          // try to re-process the now-consumed code. React Router
          // ignores replaceState (only listens to popstate), so navigation
          // out of /auth/callback is handled by the AuthCallback route
          // component itself via useNavigate on isAuthenticated change.
          onSigninCallback={() => {
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname,
            );
          }}
        >
          <OidcBridge>{children}</OidcBridge>
        </OidcAuthProvider>
      </SessionHandoff>
    );
  }
  return <StubAuthProvider>{children}</StubAuthProvider>;
}

// A tab signed out by another tab ends as the one that signed out: no kept
// pages for the next person (#1211 review, LOW 1), at the landing, and the
// full navigation drops the data cache.
tabSession?.onSignedOutElsewhere(() => {
  forgetNavPages();
  window.location.assign("/");
});

/**
 * Before the sign-in provider loads the user (lib/tab-session.ts): a new
 * tab with no user takes the one of an open tab of the app, if one answers
 * within ASK_TIMEOUT_MS, and a user near expiry or expired (after a sleep)
 * is renewed first, so the tab opens signed in (at most PREPARE_MAX_MS).
 * A tab with a fresh user, a tab that signed out, and the OIDC callback go
 * straight on.
 */
function SessionHandoff({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(
    () => tabSession === null || hasAuthParams() || !tabSession.needsPrepare(),
  );
  useEffect(() => {
    if (ready || tabSession === null) return;
    let live = true;
    void tabSession.prepareWithin().finally(() => {
      if (live) setReady(true);
    });
    return () => {
      live = false;
    };
  }, [ready]);
  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-on-surface-muted">
        Loading session…
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * Maps react-oidc-context's auth state onto our AuthState shape and
 * keeps api-client's Authorization header in sync with the OIDC
 * access_token.
 */
function OidcBridge({ children }: { children: ReactNode }) {
  const auth = useOidcAuth();

  // Push the access token into api-client synchronously during render —
  // NOT in an effect. React runs effects child-first, so an effect here
  // would set the token only AFTER the data providers nested below have
  // already fired their first fetch with no Authorization header (→ 401,
  // which the QueryClient won't retry — see App.tsx). A render-time
  // assignment runs parent-first, so currentToken is populated before any
  // descendant query mounts. It's an idempotent module write (no React
  // state), so it's safe during render. null clears the header; the
  // public login/signup endpoints correctly carry no bearer.
  setAccessToken(auth.user?.access_token ?? null);

  // Post-auth: pull the platform identity (org memberships + roles +
  // platform-admin flag) from /v1/platform/auth/me. OIDC claims don't
  // carry org membership — the platform owns that mapping. Runs once the
  // access token is set; cleared on logout.
  const [me, setMe] = useState<{
    orgs: Array<{ orgId: string; orgName: string; roles: string[]; permissions: string[]; viaVendorOrgId?: string | null; vendorName?: string | null; isVendor?: boolean; products?: SuiteProduct[] }>;
    isPlatformAdmin: boolean;
    isVendorAdmin: boolean;
    approvalStatus: "pending" | "approved" | "rejected";
    approvalSubmitted: boolean;
    displayName: string;
  } | null>(null);

  // The /auth/me → AuthState shape mapping, reusable for both the initial
  // load (effect below) and the on-demand refresh (refreshIdentity).
  const fetchMe = useCallback(async () => {
    const r = await api.get<{
      orgs: Array<{ id: string; slug: string; name: string; role: string; permissions?: string[]; via_vendor_org_id?: string | null; vendor_name?: string | null; is_vendor?: boolean; products?: SuiteProductWire[] }>;
      is_platform_admin?: boolean;
      is_vendor_admin?: boolean;
      approval_status?: "pending" | "approved" | "rejected";
      approval_submitted?: boolean;
      display_name?: string;
    }>("/v1/platform/auth/me");
    return {
      orgs: (r.orgs ?? []).map((o) => ({
        orgId: o.id,
        orgName: o.name,
        roles: [o.role],
        permissions: o.permissions ?? [],
        viaVendorOrgId: o.via_vendor_org_id ?? null,
        vendorName: o.vendor_name ?? null,
        isVendor: o.is_vendor ?? false,
        products: mapProducts(o.products),
      })),
      isPlatformAdmin: !!r.is_platform_admin,
      isVendorAdmin: !!r.is_vendor_admin,
      // Default to 'approved' when the backend omits the field — older API
      // builds + grandfathered users shouldn't be gated by a missing value.
      approvalStatus: r.approval_status ?? "approved",
      approvalSubmitted: !!r.approval_submitted,
      displayName: r.display_name ?? "",
    };
  }, []);

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.access_token) {
      setMe(null);
      return;
    }
    let cancelled = false;
    // Initial load — clear to null on a hard failure so the no-org guard works.
    fetchMe()
      .then((m) => {
        if (!cancelled) setMe(m);
      })
      .catch(() => {
        if (!cancelled) setMe(null);
      });
    // Keep role/permissions fresh WITHOUT a reload: when an admin changes a
    // member's role (or their pack/approval changes), that user's open session
    // picks it up within ~60s — and immediately when they refocus the tab —
    // so can(perm) gates re-evaluate and the nav/controls update live. No
    // websockets; a poll + focus refetch is enough. A failed REFRESH keeps the
    // existing memberships (don't blank the UI on a flaky poll).
    const refresh = () => {
      fetchMe()
        .then((m) => {
          if (!cancelled) setMe(m);
        })
        .catch(() => {
          /* transient — keep prior identity */
        });
    };
    const POLL_MS = 60_000;
    const timer = window.setInterval(refresh, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [auth.isAuthenticated, auth.user?.access_token, fetchMe]);

  const refreshIdentity = useCallback(async () => {
    if (!auth.isAuthenticated || !auth.user?.access_token) return;
    try {
      setMe(await fetchMe());
    } catch {
      /* keep existing memberships on a transient failure */
    }
  }, [auth.isAuthenticated, auth.user?.access_token, fetchMe]);

  const profile = auth.user?.profile;
  const user: User | null =
    auth.isAuthenticated && profile
      ? {
          id: profile.sub ?? "",
          email: profile.email ?? "",
          // The platform's display name first, once it is a name (not the
          // email it starts as), so a name the person sets shows at once.
          name:
            (isPersonName(me?.displayName) ? me?.displayName : undefined) ??
            (profile.name as string | undefined) ??
            (profile.preferred_username as string | undefined) ??
            profile.email ??
            "User",
          avatarUrl: (profile.picture as string | undefined) ?? null,
          orgMemberships: me?.orgs ?? [],
          isPlatformAdmin: me?.isPlatformAdmin,
          isVendorAdmin: me?.isVendorAdmin,
          approvalStatus: me?.approvalStatus,
          approvalSubmitted: me?.approvalSubmitted,
          displayName: me?.displayName,
        }
      : null;

  const value: AuthState = {
    user,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    identityLoaded: me !== null,
    signIn: (loginName, password, authRequestId) =>
      postAuth("/v1/platform/auth/login", {
        loginName,
        password,
        authRequestId,
      }),
    signUp: ({ email, password, givenName, familyName, authRequestId }) =>
      postAuth("/v1/platform/auth/signup", {
        email,
        password,
        givenName,
        familyName,
        authRequestId,
      }),
    initiateOidcRedirect: () => {
      auth.signinRedirect().catch((err) => {
        console.error("[auth] signinRedirect failed:", err);
      });
    },
    logout: () => {
      forgetNavPages(); // the next person in this tab starts from the landing (#1211 review, LOW 1)
      // Every open tab of the app signs out with this one (lib/tab-session.ts).
      const redirect = () => auth.signoutRedirect();
      void (tabSession ? tabSession.signOut(redirect) : redirect()).catch((err) => {
        console.error("[auth] signoutRedirect failed:", err);
      });
    },
    refreshIdentity,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Stub provider for the no-OIDC dev loop. signIn/signUp short-circuit
 * to STUB_USER + callbackUrl="/app" so the form components don't need
 * a separate code path.
 */
function StubAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(STUB_USER);

  useEffect(() => {
    setAccessToken(null);
  }, []);

  const value: AuthState = {
    user,
    isAuthenticated: user !== null,
    isLoading: false,
    identityLoaded: true,
    signIn: async () => {
      setUser(STUB_USER);
      return { ok: true, callbackUrl: "/app" };
    },
    signUp: async () => {
      setUser(STUB_USER);
      return { ok: true, callbackUrl: "/app" };
    },
    initiateOidcRedirect: () => {
      // No-op in stub mode — the form is the only entry point.
    },
    logout: () => {
      forgetNavPages();
      setUser(null);
    },
    refreshIdentity: async () => {
      // No-op in stub mode — STUB_USER's single org is static.
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Dev provider for a tokenless backend running AUTH_DEV_MODE (STAGING ONLY).
 * Like the stub it short-circuits the login guard (isAuthenticated = true) and
 * sends NO bearer token -- the backend's devPrincipalMiddleware injects a
 * synthetic admin principal for every request. UNLIKE the stub it calls
 * /v1/platform/auth/me, so the REAL dev-DB orgs + roles + platform-admin flag
 * drive the app (the stub's fabricated org_stub_dev never matches a real org, so
 * live data never loads under it). Gated on VITE_AUTH_DEV_MODE === "true"; a real
 * (OIDC-configured) build never reaches this branch. Pair with
 * VITE_USE_LIVE_API=true so the data layer actually fetches. The /me mapping is a
 * deliberate small copy of OidcBridge.fetchMe (kept separate to leave the proven
 * OIDC path untouched; hoist to a shared helper in a later refactor).
 */
function DevAuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<{
    orgs: User["orgMemberships"];
    isPlatformAdmin: boolean;
    isVendorAdmin: boolean;
    approvalStatus: "pending" | "approved" | "rejected";
    approvalSubmitted: boolean;
    displayName: string;
  } | null>(null);

  const fetchMe = useCallback(async () => {
    const r = await api.get<{
      orgs: Array<{ id: string; slug: string; name: string; role: string; permissions?: string[]; via_vendor_org_id?: string | null; vendor_name?: string | null; is_vendor?: boolean; products?: SuiteProductWire[] }>;
      is_platform_admin?: boolean;
      is_vendor_admin?: boolean;
      approval_status?: "pending" | "approved" | "rejected";
      approval_submitted?: boolean;
      display_name?: string;
    }>("/v1/platform/auth/me");
    return {
      orgs: (r.orgs ?? []).map((o) => ({
        orgId: o.id,
        orgName: o.name,
        roles: [o.role],
        permissions: o.permissions ?? [],
        viaVendorOrgId: o.via_vendor_org_id ?? null,
        vendorName: o.vendor_name ?? null,
        isVendor: o.is_vendor ?? false,
        products: mapProducts(o.products),
      })),
      isPlatformAdmin: !!r.is_platform_admin,
      isVendorAdmin: !!r.is_vendor_admin,
      approvalStatus: r.approval_status ?? "approved",
      approvalSubmitted: !!r.approval_submitted,
      displayName: r.display_name ?? "",
    };
  }, []);

  useEffect(() => {
    setAccessToken(null); // dev-mode API injects the principal; the SPA sends no bearer
    let cancelled = false;
    const load = () => {
      fetchMe()
        .then((m) => {
          if (!cancelled) setMe(m);
        })
        .catch(() => {
          /* transient — keep prior identity (only the first load nulls it) */
        });
    };
    fetchMe()
      .then((m) => {
        if (!cancelled) setMe(m);
      })
      .catch(() => {
        if (!cancelled) setMe(null);
      });
    const timer = window.setInterval(load, 60_000);
    window.addEventListener("focus", load);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", load);
    };
  }, [fetchMe]);

  const refreshIdentity = useCallback(async () => {
    try {
      setMe(await fetchMe());
    } catch {
      /* keep existing memberships on a transient failure */
    }
  }, [fetchMe]);

  const user: User = {
    id: "dev-user",
    email: "dev@example.com",
    name: me && isPersonName(me.displayName) ? me.displayName : "Dev (staging)",
    avatarUrl: null,
    orgMemberships: me?.orgs ?? [],
    isPlatformAdmin: me?.isPlatformAdmin,
    isVendorAdmin: me?.isVendorAdmin,
    approvalStatus: me?.approvalStatus,
    approvalSubmitted: me?.approvalSubmitted,
    displayName: me?.displayName,
  };

  const value: AuthState = {
    user,
    isAuthenticated: true,
    isLoading: false,
    identityLoaded: me !== null,
    signIn: async () => ({ ok: true, callbackUrl: "/app" }),
    signUp: async () => ({ ok: true, callbackUrl: "/app" }),
    initiateOidcRedirect: () => {
      /* no-op in dev mode */
    },
    logout: () => {
      /* no-op in dev mode — there is no real session to end */
    },
    refreshIdentity,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Route guard. Routes unauthenticated users to /login (which renders
 * Arjun's form once Zitadel has minted an AuthRequest, or immediately
 * in stub mode).
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-on-surface-muted">
        Loading session…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
